// SPDX-License-Identifier: AGPL-3.0-only
/**
 * i18next factory: i18next + IcuFormat (ICU MessageFormat for every
 * message — i18next's own plural-suffix system is disabled by the ICU
 * format plugin) + an inline lazy backend.
 *
 * The instance starts with the EAGER namespaces only: en-US's ship
 * synchronously (they are the fallback text and must never be async) and the
 * active locale's are fetched through `loadBundle` before `init` resolves —
 * the dashboard passes a Vite dynamic-import loader, so each locale becomes
 * its own chunk. A DEFERRED namespace (`studio`) is in neither set: the
 * surface that owns it calls `instance.loadNamespaces()` and waits, which is
 * what keeps the admin console's text out of every user's first load. See
 * ./resources/namespaces.ts.
 */
import i18next, {} from 'i18next';
import { IcuFormat } from './icu-format.js';
import { localeEntry, tagForLocale } from './locales.js';
import { bumpI18nRevision } from './revision.js';
import { EN_US_EAGER } from './resources/eager.js';
import { EAGER_NAMESPACES } from './resources/namespaces.js';
function lazyBackend(load) {
    return {
        type: 'backend',
        init() {
            // Options come through the closure — nothing to do.
        },
        read(language, namespace, callback) {
            void Promise.resolve()
                .then(async () => await load(language, namespace))
                .then((bundle) => {
                if (bundle === null) {
                    callback(null, {});
                    return;
                }
                const data = 'default' in bundle ? bundle.default : bundle;
                // CLONE. i18next stores a backend bundle BY REFERENCE
                // (`skipCopy: true` on the connector's store write), and this
                // object is the live default export of a dynamic-import module —
                // a process-wide singleton. Anything that later merges runtime
                // overrides into the store would rewrite the compiled chunk
                // itself, destroying the original text for every other instance
                // in the process. Clone once here so the store owns its copy.
                callback(null, structuredClone(data));
            }, () => {
                // Missing/failed chunk: resolve empty so the fallback chain applies
                // (degraded network beats a blank screen).
                callback(null, {});
            });
        },
    };
}
/**
 * Builds a ready (initialized) i18next instance for `locale`. For `en_US`
 * initialization is effectively synchronous (bundled resources); other
 * locales resolve once their bundles load (or immediately, falling back to
 * en-US, when no loader/bundle exists yet).
 */
export async function createI18n(opts) {
    const tag = tagForLocale(opts.locale);
    // DEEP clone, not a spread. A shallow spread leaves
    // `resources['en-US'].common === EN_US_EAGER.common`, and i18next's
    // ResourceStore takes `this.data = data` with no copy of its own — so the
    // store would alias the compiled ES-module singletons. Any later merge of
    // runtime overrides would then permanently rewrite the compiled
    // English in memory: the original text is gone, so even a correct
    // reset-to-built-in has nothing to restore, and on the server every
    // subsequently created instance inherits another caller's overrides.
    const resources = {
        'en-US': structuredClone(EN_US_EAGER),
    };
    for (const [resourceTag, byNs] of Object.entries(opts.resources ?? {})) {
        if (byNs === undefined)
            continue;
        const target = (resources[resourceTag] ??= {});
        for (const [ns, bundle] of Object.entries(byNs)) {
            // Cloned for the same reason as the bundled resources above: a caller
            // (Electron packaging, tests) may hand us a module singleton.
            if (bundle !== undefined)
                target[ns] = structuredClone(bundle);
        }
    }
    const instance = i18next.createInstance();
    instance.use(new IcuFormat());
    if (opts.loadBundle !== undefined)
        instance.use(lazyBackend(opts.loadBundle));
    await instance.init({
        lng: tag,
        fallbackLng: 'en-US',
        // EAGER only. `studio` joins `options.ns` the first time something calls
        // `loadNamespaces('studio')`, which is also what makes a later
        // `switchLocale` load the new language's copy of it.
        ns: [...EAGER_NAMESPACES],
        defaultNS: 'common',
        resources,
        // Bundled en-US coexists with the lazy backend for other locales.
        partialBundledLanguages: true,
        // React escapes; ICU handles placeables.
        interpolation: { escapeValue: false },
        returnNull: false,
        // TRUE since: an empty string is a deliberate override state
        // ("render nothing"), not a missing value. With `false`, an
        // admin's blank would fall through to the en-US built-in and the state
        // would be unexpressible. Compiled bundles are unaffected — the parity
        // gate asserts no compiled message is empty.
        returnEmptyString: true,
        ...(opts.onMissingKey === undefined
            ? {}
            : {
                saveMissing: true,
                missingKeyHandler: (lngs, ns, key) => {
                    for (const lng of lngs)
                        opts.onMissingKey?.(lng, ns, key);
                },
            }),
    });
    // A language switch changes what every key resolves to, so it is a revision
    // change like any other. Without this, only components that
    // consume `I18nProvider`'s context re-render — and the dashboard's ~2.3k
    // call sites use a module-level `t()` that is not a hook, so already-painted
    // strings kept the OUTGOING language until a reload while `dir` flipped
    // immediately. That produced a mirrored English UI, and it is the defect
    // `apps/e2e/tests/rtl-locale.spec.ts` used to pin as a known bug.
    instance.on('languageChanged', () => {
        bumpI18nRevision();
    });
    return instance;
}
/**
 * Preloaded live locale switch: the target locale's namespaces load BEFORE
 * `changeLanguage` fires, so the UI never renders a half-translated frame
 * while `dir`/`lang` flip.
 *
 * `loadLanguages` covers whatever is in `options.ns` at the time, which is the
 * eager set plus any deferred namespace already pulled in — so switching
 * locale inside the Studio reloads the Studio's text, and switching locale
 * outside it does not fetch a console the user has not opened.
 */
export async function switchLocale(instance, locale) {
    const { tag } = localeEntry(locale);
    if (instance.language === tag)
        return;
    await instance.loadLanguages(tag);
    await instance.changeLanguage(tag);
}
//# sourceMappingURL=create-i18n.js.map